(() => {
  const runtimeKey = '__quitchiboHeroInventoryCount';
  const existingRuntime = globalThis[runtimeKey];
  if (existingRuntime) {
    existingRuntime.init();
    return;
  }

  const rootSelector = '[data-hero-inventory-count-root]';
  const targetSelector = '[data-hero-inventory-count]';
  const countRequests = new Map();
  const cachePrefix = 'quitchibo:hero-inventory-count:';
  const cacheTtl = 5 * 60 * 1000;
  const requestTimeout = 10 * 1000;
  const maxPages = 50;

  const readCache = (endpoint) => {
    try {
      const cached = JSON.parse(localStorage.getItem(`${cachePrefix}${endpoint}`));
      if (
        !Number.isInteger(cached?.total)
        || cached.total < 0
        || !Number.isFinite(cached?.expiresAt)
        || cached.expiresAt <= Date.now()
      ) return null;
      return cached.total;
    } catch (_error) {
      return null;
    }
  };

  const writeCache = (endpoint, total) => {
    try {
      localStorage.setItem(
        `${cachePrefix}${endpoint}`,
        JSON.stringify({ total, expiresAt: Date.now() + cacheTtl }),
      );
    } catch (_error) {
      // Storage can be unavailable in private browsing; the live request still succeeds.
    }
  };

  const fetchPage = async (endpoint, page, cacheBucket, signal) => {
    const url = new URL(endpoint, location.origin);
    url.searchParams.set('page', String(page));
    url.searchParams.set('_hero_count', String(cacheBucket));

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      signal,
    });

    if (!response.ok) throw new Error(`Inventory count request failed: ${response.status}`);

    const payload = await response.json();
    if (payload.valid !== true) throw new Error('Inventory count helpers unavailable');

    const count = Number(payload.count);
    const currentPage = Number(payload.page);
    const pages = Number(payload.pages);

    if (!Number.isInteger(count) || count < 0) throw new Error('Invalid inventory count');
    if (!Number.isInteger(currentPage) || currentPage !== page) throw new Error('Invalid inventory page');
    if (!Number.isInteger(pages) || pages < 1 || pages > maxPages) throw new Error('Invalid inventory page total');

    return { count, pages };
  };

  const fetchTotal = async (endpoint, signal) => {
    const cacheBucket = Math.floor(Date.now() / cacheTtl);
    const firstPage = await fetchPage(endpoint, 1, cacheBucket, signal);
    const remainingRequests = [];

    for (let page = 2; page <= firstPage.pages; page += 1) {
      remainingRequests.push(fetchPage(endpoint, page, cacheBucket, signal));
    }

    const subtotals = await Promise.all(remainingRequests);
    let total = firstPage.count;

    subtotals.forEach((subtotal) => {
      if (subtotal.pages !== firstPage.pages) throw new Error('Inventory pagination changed');
      total += subtotal.count;
    });

    return total;
  };

  const fetchTotalWithTimeout = (endpoint) => {
    const controller = new AbortController();
    let settled = false;
    let timeoutId;

    return new Promise((resolve, reject) => {
      const settle = (callback, value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        callback(value);
      };

      timeoutId = setTimeout(() => {
        if (settled) return;
        controller.abort();
        settle(reject, new Error('Inventory count request timed out'));
      }, requestTimeout);

      fetchTotal(endpoint, controller.signal).then(
        (total) => settle(resolve, total),
        (error) => {
          if (!settled) controller.abort();
          settle(reject, error);
        },
      );
    });
  };

  const getFreshTotal = (endpoint) => {
    if (!countRequests.has(endpoint)) {
      const request = fetchTotalWithTimeout(endpoint).then((total) => {
        writeCache(endpoint, total);
        return total;
      });
      countRequests.set(endpoint, request);
      const releaseRequest = () => {
        if (countRequests.get(endpoint) === request) countRequests.delete(endpoint);
      };
      request.then(releaseRequest, releaseRequest);
    }

    return countRequests.get(endpoint);
  };

  const initRoot = (root) => {
    if (root.dataset.heroInventoryInitialized === 'true') return;

    const endpoint = root.dataset.heroInventoryEndpoint;
    const targets = root.querySelectorAll(targetSelector);
    if (!endpoint || !targets.length) return;

    root.dataset.heroInventoryInitialized = 'true';

    const locale = document.documentElement.lang || 'de-DE';
    const formatTotal = (total) => new Intl.NumberFormat(locale).format(total);
    const renderTotal = (total) => {
      const formattedTotal = formatTotal(total);
      targets.forEach((target) => {
        if (target.textContent !== formattedTotal) target.textContent = formattedTotal;
      });
    };
    const cachedTotal = readCache(endpoint);

    if (cachedTotal !== null) {
      renderTotal(cachedTotal);
      root.dataset.heroInventoryState = 'refreshing';
    } else {
      root.dataset.heroInventoryState = 'loading';
    }

    getFreshTotal(endpoint).then(
      (total) => {
        renderTotal(total);
        root.dataset.heroInventoryState = 'ready';
      },
      () => {
        if (cachedTotal !== null) {
          root.dataset.heroInventoryState = 'stale';
          return;
        }

        const fallbackTotal = Number(root.dataset.heroInventoryFallback);
        if (Number.isInteger(fallbackTotal) && fallbackTotal >= 0) renderTotal(fallbackTotal);
        root.dataset.heroInventoryState = 'fallback';
      },
    );
  };

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches?.(rootSelector)) roots.push(scope);
    scope.querySelectorAll?.(rootSelector).forEach((root) => roots.push(root));
    roots.forEach((root) => void initRoot(root));
  };

  globalThis[runtimeKey] = { init };

  init();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
